import { ValidationError } from '@/hooks/leave/useLeaveRequestValidation';
import { ValidationFeedback } from '@/components/leave/enhanced/ValidationFeedback';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useMyLeaveBalances } from '@/hooks/leave/useLeaveBalances';
import { 
  AlertTriangle, 
  Info, 
  Lightbulb, 
  Calendar,
  Clock,
  TrendingUp 
} from 'lucide-react';

interface EnhancedValidationFeedbackProps {
  errors: ValidationError[];
  isValidating: boolean;
  isValid: boolean;
  leaveType: string;
  startDate: string;
  endDate: string;
  calculatedDays: number;
  isPartDay?: boolean;
  requestedHours?: number;
}

export function EnhancedValidationFeedback({
  errors,
  isValidating,
  isValid,
  leaveType,
  startDate,
  endDate,
  calculatedDays,
  isPartDay,
  requestedHours
}: EnhancedValidationFeedbackProps) {
  const { data: balances } = useMyLeaveBalances();
  
  const currentBalance = balances?.find(b => b.policy.leave_type === leaveType);
  const remainingBalance = currentBalance ? currentBalance.current_balance_days - calculatedDays : 0;

  // Smart suggestions based on validation state
  const suggestions = [];
  
  if (currentBalance && remainingBalance < 2 && remainingBalance >= 0) {
    suggestions.push({
      type: 'warning',
      title: 'Low Balance Warning',
      message: 'This request will leave you with very few days remaining. Consider planning future leave carefully.',
      action: 'View Balance Projection'
    });
  }

  if (startDate && endDate) {
    const start = new Date(startDate);
    const dayOfWeek = start.getDay();
    
    // Suggest extending to cover weekends
    if (dayOfWeek === 1 && calculatedDays >= 4) { // Monday start, 4+ days
      suggestions.push({
        type: 'tip',
        title: 'Weekend Extension',
        message: 'Consider starting on Friday to include the weekend at no extra cost',
        action: null
      });
    }
  }

  return (
    <div className="space-y-4">
      {/* Standard validation feedback */}
      <ValidationFeedback 
        errors={errors}
        isValidating={isValidating}
      />

      {/* Enhanced balance visualization */}
      {currentBalance && leaveType && calculatedDays > 0 && (
        <Card className="border-primary/20">
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium capitalize">{leaveType} Balance</h4>
                  <p className="text-sm text-muted-foreground">
                    Available: {currentBalance.current_balance_days} days
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">
                    {remainingBalance}
                  </div>
                  <div className="text-xs text-muted-foreground">days after request</div>
                </div>
              </div>
              
              <Progress 
                value={Math.max(0, (remainingBalance / (currentBalance.current_balance_days + calculatedDays)) * 100)} 
                className="h-2"
              />
              
              {remainingBalance < 0 && (
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span>This request would overdraw your balance by {Math.abs(remainingBalance)} days</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-primary">Smart Suggestions</h4>
            </div>
            
            <div className="space-y-3">
              {suggestions.map((suggestion, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {suggestion.type === 'warning' && <AlertTriangle className="h-4 w-4 text-warning" />}
                    {suggestion.type === 'info' && <Info className="h-4 w-4 text-primary" />}
                    {suggestion.type === 'tip' && <TrendingUp className="h-4 w-4 text-success" />}
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="font-medium text-sm">{suggestion.title}</p>
                    <p className="text-sm text-muted-foreground">{suggestion.message}</p>
                    {suggestion.action && (
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                        {suggestion.action}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick stats for valid requests */}
      {isValid && currentBalance && calculatedDays > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-success" />
              <span className="text-sm font-medium">Duration</span>
            </div>
            <p className="text-lg font-bold text-success">
              {isPartDay && requestedHours 
                ? `${requestedHours}h (${calculatedDays.toFixed(2)} days)` 
                : `${calculatedDays} days`}
            </p>
          </div>
          
          <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Remaining</span>
            </div>
            <p className={`text-lg font-bold ${remainingBalance < 0 ? 'text-destructive' : 'text-primary'}`}>
              {remainingBalance} days
            </p>
          </div>
        </div>
      )}

      {/* Policy context (if available) */}
      {currentBalance && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 text-xs text-muted-foreground cursor-help">
                <Info className="h-3 w-3" />
                <span>Policy: {currentBalance.policy.name}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                <p>Available: {currentBalance.current_balance_days} days</p>
                <p>Policy: {currentBalance.policy.name}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}